import React from 'react';
import SEO from '../components/SEO';

const CLanguage: React.FC = () => {
  return (
    <>
      <SEO 
        title="C Language Training | C Programming Course with Certification - SBRIT Solution"
        description="Master C programming fundamentals with hands-on coding practice. Learn C language from basics to advanced concepts for system programming and embedded development."
        keywords="c language training, c programming course, c programming fundamentals, c certification, c for beginners, system programming, embedded programming, c coding"
        url="https://sbritsolution.in/courses/c-language"
        type="article"
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                C Language Training
              </h1>
              <p className="text-xl md:text-2xl mb-8 leading-relaxed">
                Master C Programming Fundamentals | System Programming | Embedded Development
              </p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Start Learning - ₹8,999
                </button>
                <button className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Download Syllabus
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Why C Language */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Why Learn C Language?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">System Programming</h3>
                <p className="text-gray-600">C is the foundation for system programming, operating systems, and embedded systems development.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🎯</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Performance</h3>
                <p className="text-gray-600">C provides direct memory access and high performance, making it ideal for resource-intensive applications.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🔧</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Foundation Language</h3>
                <p className="text-gray-600">Understanding C helps you learn other programming languages and computer architecture concepts.</p>
              </div>
            </div>
          </div>
        </div>

        {/* C Language Curriculum */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Complete C Language Curriculum</h2>
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-blue-600">C Fundamentals</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Introduction to C Programming</li>
                    <li>• C Compiler & IDE Setup</li>
                    <li>• Data Types & Variables</li>
                    <li>• Operators & Expressions</li>
                    <li>• Control Structures</li>
                    <li>• Loops & Iteration</li>
                    <li>• Arrays & Strings</li>
                    <li>• Functions & Scope</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-indigo-600">Advanced C Concepts</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Pointers & Memory Management</li>
                    <li>• Structures & Unions</li>
                    <li>• File Handling</li>
                    <li>• Dynamic Memory Allocation</li>
                    <li>• Preprocessor Directives</li>
                    <li>• Error Handling</li>
                    <li>• Advanced Data Structures</li>
                    <li>• System Programming Basics</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-purple-600">Practical Applications</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Console Applications</li>
                    <li>• File Processing Programs</li>
                    <li>• Data Structure Implementation</li>
                    <li>• Algorithm Implementation</li>
                    <li>• System Utilities</li>
                    <li>• Embedded Programming Intro</li>
                    <li>• Performance Optimization</li>
                    <li>• Debugging Techniques</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-green-600">Tools & Best Practices</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• GCC Compiler</li>
                    <li>• GDB Debugger</li>
                    <li>• Make & Build Systems</li>
                    <li>• Code Organization</li>
                    <li>• Documentation Standards</li>
                    <li>• Testing Strategies</li>
                    <li>• Version Control</li>
                    <li>• Industry Best Practices</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Course Features */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">What You'll Get</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">📚</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Comprehensive Content</h3>
                <p className="text-gray-600 text-sm">40+ hours of structured learning with practical examples</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-indigo-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">💻</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Hands-on Projects</h3>
                <p className="text-gray-600 text-sm">Real-world projects to build your portfolio</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">🎓</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Certification</h3>
                <p className="text-gray-600 text-sm">Industry-recognized certificate upon completion</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">👥</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Expert Support</h3>
                <p className="text-gray-600 text-sm">24/7 support from industry experts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Career Opportunities */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Career Opportunities</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <h3 className="text-xl font-semibold mb-4 text-blue-600">System Programmer</h3>
                <p className="text-gray-600 mb-4">Develop operating systems, device drivers, and system utilities</p>
                <div className="text-sm text-gray-500">Average Salary: ₹6-12 LPA</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <h3 className="text-xl font-semibold mb-4 text-indigo-600">Embedded Developer</h3>
                <p className="text-gray-600 mb-4">Work on IoT devices, microcontrollers, and embedded systems</p>
                <div className="text-sm text-gray-500">Average Salary: ₹5-10 LPA</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <h3 className="text-xl font-semibold mb-4 text-purple-600">Software Engineer</h3>
                <p className="text-gray-600 mb-4">Build high-performance applications and software solutions</p>
                <div className="text-sm text-gray-500">Average Salary: ₹4-8 LPA</div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Master C Programming?</h2>
            <p className="text-xl mb-8">Join thousands of developers who started their programming journey with C</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Enroll Now - ₹8,999
              </button>
              <button className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Download Course Brochure
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CLanguage;
